// 🔧 fixed/updated by ChatGPT on 2025-10-18 18:18:00 – reason: remove duplicate <Header/> in mid-section, restore full features layout, and keep single header at top (START)
import React, { useState } from "react";
import Header from "../../components/Header/Header";
import hero3 from "../../assets/images/hero3.png";
import hero4 from "../../assets/images/hero4.png";
import {
  PiNumberSquareOneLight,
  PiNumberSquareThreeLight,
  PiNumberSquareTwoLight,
} from "react-icons/pi";
import { FaCode, FaLaughWink } from "react-icons/fa";
import { MdSupportAgent } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { logger } from "../../utils/logger"; // added unified logger
import "./homepage.css";

// 📘 For business users: This page is the first thing visitors see. It highlights features, shows how Mudumbai works, and collects contact details.
logger.log("📂 [Homepage.jsx] File started - Rendering public landing page");

const Homepage = () => {
  logger.log("➡️ [Homepage] Component start - initializing state and navigation");

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  // 🧠 For business users: This function runs whenever someone types into a contact form field.
  const handleChange = (e) => {
    logger.log("➡️ [Homepage] handleChange() started", { field: e.target.name });
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
    logger.log("⬅️ [Homepage] handleChange() ended - field updated", { field: name });
  };

  // 🧩 For business users: This function runs when the visitor submits the contact form.
  const handleSubmit = (e) => {
    logger.log("➡️ [Homepage] handleSubmit() started");
    e.preventDefault();
    const newErrors = {};
    ["name", "email", "phone", "company", "message"].forEach((f) => {
      if (!formData[f]) newErrors[f] = `${f} is required`;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      logger.log("✅ [Homepage] Form submitted successfully", formData);
    } else {
      logger.warn("⚠️ [Homepage] Validation failed - missing fields", newErrors);
    }
    logger.log("⬅️ [Homepage] handleSubmit() ended");
  };

  logger.log("✅ [Homepage] Component rendering started");

  return (
    <>
      {/* ✅ render Header once at the top */}
      <Header />

      <main className="main-bg">
        {/* Hero Section */}
        <section className="section" id="home">
          <div className="blob1"></div>
          <div className="blob2"></div>
          <div className="container">
            <div className="flex-row">
              <div className="text-left mb-12" style={{ flex: 1 }}>
                <h1 className="title mb-4">Build Apps Without Code!</h1>
                <p className="subtitle mb-10">
                  Are you ready to turn your ideas into reality without writing a single
                  line of code? Look no further! MUDUMBAI empowers individuals with all
                  levels of technical expertise to create custom mobile and web
                  applications effortlessly.
                </p>
                <button
                  onClick={(event) => {
                    logger.log("➡️ [Homepage] Get Started button clicked");
                    event.preventDefault();
                    navigate("/auth/signup");
                    logger.log("⬅️ [Homepage] Navigated to signup");
                  }}
                  className="btn"
                >
                  Get Started
                </button>
              </div>
              <div style={{ flex: 1 }}>
                <img
                  src={hero4}
                  alt="Hero Illustration"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section bg-primary-light" id="features">
          <div className="container">
            <div className="text-center mb-20">
              <h4 className="color-secondary mb-4" style={{ fontWeight: 700 }}>
                Our Features
              </h4>
              <h1 className="title color-white">
                Code-Free, Stress-Free: MUDUMBAI's Promise.
              </h1>
            </div>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">
                  <FaCode className="icon-size" />
                </div>
                <h3 className="subtitle color-primary-dark">No-code Platform</h3>
                <p>
                  That allows anyone, regardless of coding experience, to design,
                  develop, and deploy fully functional applications.
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <FaLaughWink className="icon-size" />
                </div>
                <h3 className="subtitle color-primary-dark">Too Convenient</h3>
                <p>With MUDUMBAI, building apps is as easy as filling out a form.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <MdSupportAgent className="icon-size" />
                </div>
                <h3 className="subtitle color-primary-dark">Support 24/7</h3>
                <p>Our dedicated support team is here to help you every step of the way.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Saving Money Section */}
        <section className="section" id="saving-money">
          <div className="container">
            <div className="blob1"></div>
            <div className="blob2"></div>
            <div className="flex-row">
              <div style={{ flex: 1 }}>
                <img
                  src={hero3}
                  alt="Saving Money"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
              <div className="text-left" style={{ flex: 1 }}>
                <h4 className="color-secondary mb-4" style={{ fontWeight: 700 }}>
                  Crafting Dreams, One Click at a Time.
                </h4>
                <h1 className="title mb-4">We help you to turn your vision into reality!</h1>
                <p className="subtitle mb-10">
                  Are you ready to turn your ideas into reality without writing a single
                  line of code? Look no further! MUDUMBAI empowers individuals with all
                  levels of technical expertise to create custom mobile and web
                  applications effortlessly.
                </p>
                <button className="btn">Read More</button>
              </div>
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="section bg-primary-light" id="how-it-works">
          <div className="container">
            <div className="text-center mb-20">
              <h4 className="color-secondary mb-4" style={{ fontWeight: 700 }}>
                How It Works
              </h4>
              <h1 className="title color-white">Effortless Website Creation</h1>
            </div>
            <div className="flex-row" style={{ gap: "16px" }}>
              <div className="feature-card">
                <div className="feature-icon">
                  <PiNumberSquareOneLight className="icon-size" />
                </div>
                <h3 className="subtitle color-primary-dark">Signup with Mudumbai</h3>
                <p>
                  Create your Mudumbai account and help us to know about your
                  requirements.
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <PiNumberSquareTwoLight className="icon-size" />
                </div>
                <h3 className="subtitle color-primary-dark">Setup Your Dashboard</h3>
                <p>
                  We provide your easy-to-use dashboard; customize it according to your
                  project.
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <PiNumberSquareThreeLight className="icon-size" />
                </div>
                <h3 className="subtitle color-primary-dark">Enjoy The Features!</h3>
                <p>
                  Dive into a vast collection of templates designed to suit every taste
                  and purpose.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="section" id="contact">
          <div className="container">
            <div className="text-center mb-20">
              <h4 className="color-secondary mb-4" style={{ fontWeight: 700 }}>
                Have A Question
              </h4>
              <h1 className="title">Get In Touch</h1>
            </div>
            <form onSubmit={handleSubmit} className="contact-form text-center">
              <div className="form-row">
                <div className="input-group">
                  <input
                    type="text"
                    name="name"
                    className="input"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && <p className="text-error">{errors.name}</p>}
                </div>
                <div className="input-group">
                  <input
                    type="email"
                    name="email"
                    className="input"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <p className="text-error">{errors.email}</p>}
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <input
                    type="tel"
                    name="phone"
                    className="input"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && <p className="text-error">{errors.phone}</p>}
                </div>
                <div className="input-group">
                  <input
                    type="text"
                    name="company"
                    className="input"
                    placeholder="Company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                  {errors.company && <p className="text-error">{errors.company}</p>}
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <textarea
                    rows="4"
                    name="message"
                    className="textarea"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                  {errors.message && <p className="text-error">{errors.message}</p>}
                </div>
              </div>
              <button className="btn" type="submit">
                Send Message
              </button>
            </form>
          </div>
        </section>
      </main>
    </>
  );
  logger.log("⬅️ [Homepage] Component end - finished rendering page");
};

logger.log("✅ [Homepage.jsx] File executed successfully - user interface rendered");
export default Homepage;
// 🔧 fixed/updated by ChatGPT on 2025-10-18 18:18:00 – reason: remove duplicate <Header/> in mid-section, restore full features layout, and keep single header at top (END)
